import React, { useState, useEffect, useCallback } from 'react';
import { Users, Gift, Percent, Settings, Clock, TrendingUp } from 'lucide-react';
import { Card } from '../components/ui/Card';
import apiClient from '../services/apiConfig';

interface DashboardStats {
  totalPassengers: number;
  totalPromotions: number;
  totalRewards: number;
  totalDiscounts: number;
  pendingRedemptions: number;
  newUsersThisWeek: number;
}

interface RecentActivity {
  id: string;
  type: 'registration' | 'redemption' | 'promotion';
  title: string;
  subtitle: string;
  time: string;
  icon: 'user' | 'gift' | 'percent';
}

const NewHomePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalPassengers: 0,
    totalPromotions: 0,
    totalRewards: 0,
    totalDiscounts: 0,
    pendingRedemptions: 0,
    newUsersThisWeek: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch passengers
      const passengersRes = await apiClient.get('/passengers?per_page=1000');
      let passengersList = [];
      if (passengersRes.data.data) {
        passengersList = passengersRes.data.data;
      } else if (Array.isArray(passengersRes.data)) {
        passengersList = passengersRes.data;
      }

      // Fetch promotions
      const promotionsRes = await apiClient.get('/promotions');
      const promotions = promotionsRes.data.success ? promotionsRes.data.data : [];
      const activePromotions = promotions.filter((p: any) => p.is_active);

      // Fetch rewards
      const rewardsRes = await apiClient.get('/rewards');
      const rewards = rewardsRes.data.success ? rewardsRes.data.data : [];
      const activeRewards = rewards.filter((r: any) => r.is_active);

      // Fetch discounts
      const discountsRes = await apiClient.get('/discounts');
      const discounts = discountsRes.data.success ? discountsRes.data.data : [];
      const activeDiscounts = discounts.filter((d: any) => d.is_active);

      // Calculate new users this week
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const newUsersThisWeek = passengersList.filter((p: any) => {
        return new Date(p.created_at) >= oneWeekAgo;
      }).length;

      // Try to fetch redemptions
      let pendingRedemptions = 0;
      try {
        const rewardRedemptionsRes = await apiClient.get('/rewards/redemptions/my');
        const promotionRedemptionsRes = await apiClient.get('/promotion-redemptions/my');

        const allRedemptions = [
          ...(rewardRedemptionsRes.data.success ? rewardRedemptionsRes.data.data : []),
          ...(promotionRedemptionsRes.data.success ? promotionRedemptionsRes.data.data : [])
        ];

        pendingRedemptions = allRedemptions.filter((r: any) => r.status === 'pending').length;
      } catch (error) {
        console.log('Could not fetch redemptions');
      }

      setStats({
        totalPassengers: passengersList.length,
        totalPromotions: activePromotions.length,
        totalRewards: activeRewards.length,
        totalDiscounts: activeDiscounts.length,
        pendingRedemptions,
        newUsersThisWeek,
      });

      // Build recent activity
      const activity: RecentActivity[] = [];

      // Add recent registrations
      passengersList.slice(0, 2).forEach((p: any, index: number) => {
        activity.push({
          id: `registration-${p.id}-${index}`,
          type: 'registration',
          title: 'Nuevo huésped registrado',
          subtitle: `${p.name} ${p.last_name || ''} - ${(p.category || 'classic').toUpperCase()}`,
          time: getTimeAgo(p.created_at),
          icon: 'user',
        });
      });

      // Add recent promotions
      if (activePromotions.length > 0) {
        const recent = activePromotions[0];
        activity.push({
          id: `promotion-${recent.id}`,
          type: 'promotion',
          title: 'Promoción activa',
          subtitle: recent.title,
          time: getTimeAgo(recent.created_at),
          icon: 'percent',
        });
      }

      setRecentActivity(activity.slice(0, 4));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} horas`;
    return `Hace ${diffDays} días`;
  };

  const statCards = [
    { icon: Users, label: 'Total Huéspedes', value: stats.totalPassengers.toString(), color: 'bg-blue-600' },
    { icon: Gift, label: 'Premios Activos', value: stats.totalRewards.toString(), color: 'bg-emerald-600' },
    { icon: Percent, label: 'Descuentos Activos', value: stats.totalDiscounts.toString(), color: 'bg-violet-600' },
    { icon: Settings, label: 'Promociones', value: stats.totalPromotions.toString(), color: 'bg-orange-600' },
    { icon: Clock, label: 'Canjes Pendientes', value: stats.pendingRedemptions.toString(), color: 'bg-red-600' },
    { icon: TrendingUp, label: 'Nuevos Esta Semana', value: stats.newUsersThisWeek.toString(), color: 'bg-indigo-600' },
  ];

  const getActivityIcon = (icon: string) => {
    switch (icon) {
      case 'user':
        return { Icon: Users, colorClass: 'bg-blue-100 text-blue-600' };
      case 'gift':
        return { Icon: Gift, colorClass: 'bg-emerald-100 text-emerald-600' };
      case 'percent':
        return { Icon: Percent, colorClass: 'bg-orange-100 text-orange-600' };
      default:
        return { Icon: Users, colorClass: 'bg-gray-100 text-gray-600' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Bienvenido al Panel</h1>
        <p className="text-sm text-slate-600 mt-1">Sistema de gestión Hotel Chiavari</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <div className="flex items-center gap-3">
                <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center text-white`}>
                  <Icon size={20} />
                </div>
                <div>
                  <p className="text-xs text-slate-600 font-medium">{stat.label}</p>
                  <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Actividad Reciente</h2>
        </div>
        {recentActivity.length > 0 ? (
          <div className="space-y-0 divide-y divide-slate-100">
            {recentActivity.map((activity) => {
              const { Icon, colorClass } = getActivityIcon(activity.icon);
              return (
                <div key={activity.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 ${colorClass} rounded-lg flex items-center justify-center`}>
                      <Icon size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{activity.title}</p>
                      <p className="text-xs text-slate-500">{activity.subtitle}</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">{activity.time}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-slate-500 py-8">No hay actividad reciente</p>
        )}
      </Card>
    </div>
  );
};

export default NewHomePage;
